import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  createSponsorImpression,
  createSponsorClick,
  trackAdPixel,
} from "core/utils/tracker";
import ButtonBase from "@mui/material/ButtonBase";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { nanoid } from "nanoid";
import {
  trackBrandingEvent,
  BRANDING_IMPRESSION,
  BRANDING_CLICK,
} from "core/utils/adAnalytics";
import {
  CORNER_SCALE,
  getBrandingVideoRect,
} from "core/components/PlayerInView/PlayerInView";

/**
 * Инстрим-брендинг: рамка-креатив на весь плеер под видео. Видео ужимается до
 * 80% от нижнего края (PlayerInView), сверху и по бокам открывается рамка.
 *
 * Картинка рамки сначала полностью загружается, и только потом засчитывается
 * показ и начинается поджатие: раньше фон грузился во время анимации, и вокруг
 * ужимающегося видео была видна чернота.
 *
 * С заливкой фона (fillThumbnails) рамка не обрезается: креатив вписывается
 * целиком так, что его окно под видео ложится ровно на видео (getFrameLayout),
 * а пустые края экрана не 16:9 закрывает заливка. Без заливки как раньше:
 * креатив на весь плеер (cover) и обрезается по краям.
 */
const CreativeLayer = styled("div")({
  position: "absolute",
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 100%",
  pointerEvents: "none",
});

const StyledButtonBase = styled(ButtonBase)(() => ({
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  height: "100%",
  width: "100%",
  zIndex: 1,
  backgroundPosition: "top center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
}));

// Ссылка рамки поверх слоёв плеера: шапка с названием лежит над верхней полосой
// рамки, мигала при движении мыши, давала текстовый курсор и забирала клики.
// Вырез: само видео и полоса панели управления под ним (frameClipPath).
const FOOTER_RESERVED_PX = 72;
const FRAME_LINK_Z_INDEX = 3;

const FrameLink = styled(ButtonBase)({
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  zIndex: FRAME_LINK_Z_INDEX,
  cursor: "pointer",
  background: "transparent",
});

const px = (n) => `${Math.round(n * 10) / 10}px`;

/**
 * clip-path кликабельной рамки в пикселях плеера: весь плеер минус видео и
 * полоса панели управления внизу ужатого блока (панель лежит в нём и
 * уменьшается вместе с ним). Раньше вырезался весь блок CORNER_SCALE, и
 * видимая рамка вокруг вписанного видео не кликалась: в вертикальном плеере
 * почти вся рамка над видео, в широком поля сбоку от видео.
 */
export function frameClipPath({ width, height }) {
  const video = getBrandingVideoRect({ width, height });
  const boxLeft = (width * (1 - CORNER_SCALE)) / 2;
  const boxRight = width - boxLeft;
  const footerTop = height - FOOTER_RESERVED_PX * CORNER_SCALE;
  const videoRight = video.x + video.width;

  const outer = [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
    [0, 0],
  ];
  const hole = [
    [video.x, video.y],
    [videoRight, video.y],
    [videoRight, footerTop],
    [boxRight, footerTop],
    [boxRight, height],
    [boxLeft, height],
    [boxLeft, footerTop],
    [video.x, footerTop],
    [video.x, video.y],
  ];

  return `polygon(evenodd, ${[...outer, ...hole]
    .map(([x, y]) => `${px(x)} ${px(y)}`)
    .join(", ")})`;
}

const SHOW_BANNER_TIMEOUT = 15000; // Через сколько мс закрывать баннер
const ANIMATION_DURATION = 1000; // Длительность анимации (как у поджатия в PlayerInView)

const IMPRESSION = "IMPRESSION";
const DEFAULT = "DEFAULT";
const MEDIUM = "MEDIUM";
const HIGH = "HIGH";
const MAXRES = "MAXRES";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

function findImageUrl(formats, { smUp, mdUp, lgUp }) {
  const indexByFormat = formats.reduce((all, item) => {
    all[item.format] = item.url;
    return all;
  }, {});

  if (lgUp && indexByFormat[MAXRES]) return indexByFormat[MAXRES];
  if (mdUp && indexByFormat[HIGH]) return indexByFormat[HIGH];
  if (smUp && indexByFormat[MEDIUM]) return indexByFormat[MEDIUM];
  if (indexByFormat[DEFAULT]) return indexByFormat[DEFAULT];

  return indexByFormat[MAXRES];
}

/**
 * Рамка под пропорции плеера: в вертикальном плеере (выше, чем шире) вертикальный
 * вариант, если он загружен. Горизонтальная рамка там сильно обрезается.
 */
export function pickBrandingThumbnails({
  thumbnails,
  verticalThumbnails,
  width,
  height,
}) {
  if (width > 0 && height > width && verticalThumbnails?.length) {
    return verticalThumbnails;
  }

  return thumbnails || [];
}

/**
 * Окно под видео в макете креатива (px): горизонтальный 1920x1080, вертикальный
 * 1080x1920. Рамка вокруг видео: 10% по бокам, 20% сверху, видео 16:9 внизу.
 */
export const CREATIVE_TEMPLATES = {
  horizontal: { width: 1920, height: 1080, x: 192, y: 216, videoWidth: 1536 },
  vertical: { width: 1080, height: 1920, x: 108, y: 1434, videoWidth: 864 },
};

/**
 * Раскладка рамки с заливкой: где видео и куда положить креатив, чтобы его
 * окно под видео совпало с видео. Вертикальный креатив берём только в плеере
 * 9:16 и уже: в менее вытянутом (iPad вертикально) он обрезался бы сверху,
 * а горизонтальный вписывается в любой плеер. variant задаёт вариант явно:
 * раскладка по уже загруженной картинке, пока грузится другая.
 */
export function getFrameLayout({
  width,
  height,
  hasVertical = true,
  variant: forcedVariant,
}) {
  const video = getBrandingVideoRect({ width, height });
  const variant =
    forcedVariant ||
    (hasVertical && height * 9 >= width * 16 ? "vertical" : "horizontal");
  const template = CREATIVE_TEMPLATES[variant];
  const k = video.width / template.videoWidth;

  return {
    variant,
    video,
    creative: {
      x: video.x - template.x * k,
      y: video.y - template.y * k,
      width: template.width * k,
      height: template.height * k,
    },
  };
}

function preloadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = resolve;
    image.onerror = reject;
    image.src = url;
  });
}

export default function SponsorCornerBanner({
  onStart,
  onFinish,
  setCornerBannerHeight,
  cornerBanner,
}) {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const bannerRef = useRef();
  const timersRef = useRef([]);
  const startedRef = useRef(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Загруженный показ: картинка рамки, заливка (если есть) и вариант раскладки
  const [loaded, setLoaded] = useState(null);
  const [active, setActive] = useState(false);

  const {
    token,
    thumbnails,
    verticalThumbnails,
    fillThumbnails,
    link,
    isClickable,
    clickPixel,
    additionalClickPixels,
    impressionPixel,
    additionalImpressionPixels,
  } = cornerBanner;

  // Размер плеера (рамка на весь плеер): вариант выбираем до загрузки картинки,
  // раскладку с заливкой пересчитываем при смене размера (полноэкранный режим)
  useIsomorphicLayoutEffect(() => {
    const node = bannerRef.current;
    if (!node) return undefined;

    const measure = () =>
      setSize({ width: node.offsetWidth, height: node.offsetHeight });

    measure();

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const fillSrc = fillThumbnails?.length
    ? findImageUrl(fillThumbnails, { smUp, mdUp, lgUp })
    : null;

  // Что грузить: вариант креатива и с заливкой ли. С заливкой решаем один раз
  // на показ, вариант пересчитываем при повороте экрана.
  const plan = useMemo(() => {
    if (!size.width) return null;

    let variant;
    if (fillSrc) {
      variant = getFrameLayout({
        ...size,
        hasVertical: Boolean(verticalThumbnails?.length),
      }).variant;
    } else {
      variant =
        pickBrandingThumbnails({ thumbnails, verticalThumbnails, ...size }) ===
        verticalThumbnails
          ? "vertical"
          : "horizontal";
    }

    const selected =
      variant === "vertical" ? verticalThumbnails : thumbnails || [];

    const src = selected?.length
      ? findImageUrl(selected, { smUp, mdUp, lgUp })
      : null;

    return src ? { src, variant, fillSrc } : null;
  }, [size, thumbnails, verticalThumbnails, fillSrc, smUp, mdUp, lgUp]);

  // Предзагрузка: показ стартует только с готовыми картинками. Если заливка не
  // загрузилась, показываем рамку по-старому (на весь плеер).
  useEffect(() => {
    if (!plan || startedRef.current) return undefined;

    let cancelled = false;

    const start = (fill) => {
      startedRef.current = true;
      setLoaded({ src: plan.src, variant: plan.variant, fill });

      createSponsorImpression(token, "In-Stream Branding", IMPRESSION);

      trackBrandingEvent(BRANDING_IMPRESSION, { format: "frame" });

      trackAdPixel(impressionPixel, additionalImpressionPixels);

      onStart();

      // Рамка уже нарисована под видео: поджатие открывает готовую картинку
      window.requestAnimationFrame(() => {
        setCornerBannerHeight(bannerRef.current?.offsetHeight || 1);
        setActive(true);
      });

      timersRef.current.push(
        setTimeout(() => {
          setActive(false);
          setCornerBannerHeight(null);
        }, SHOW_BANNER_TIMEOUT - ANIMATION_DURATION),
        setTimeout(() => {
          setCornerBannerHeight(null);
          onFinish();
        }, SHOW_BANNER_TIMEOUT)
      );
    };

    const load = async () => {
      const fill = plan.fillSrc
        ? preloadImage(plan.fillSrc).then(
            () => plan.fillSrc,
            () => null
          )
        : null;

      try {
        await preloadImage(plan.src);
      } catch (error) {
        if (cancelled || startedRef.current) return;
        startedRef.current = true;
        onFinish();
        return;
      }

      const loadedFill = await fill;

      if (cancelled || startedRef.current) return;
      start(loadedFill);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [plan]);

  // Поворот экрана во время показа: догружаем картинку другого варианта и
  // подменяем, показ заново не считаем. До загрузки остаётся прежняя.
  useEffect(() => {
    if (!plan || !loaded || plan.src === loaded.src) return undefined;

    let cancelled = false;

    const swap = async () => {
      try {
        await preloadImage(plan.src);
      } catch (error) {
        return;
      }

      if (cancelled) return;
      setLoaded(
        (current) =>
          current && { ...current, src: plan.src, variant: plan.variant }
      );
    };

    swap();

    return () => {
      cancelled = true;
    };
  }, [plan, loaded]);

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      setCornerBannerHeight(null);
    },
    [setCornerBannerHeight]
  );

  // Клик обычной ссылкой: window.open WebView приложений (iOS) игнорирует
  const clickId = useMemo(() => nanoid(), []);
  const href =
    isClickable && link ? link.replace("{clickId}", clickId) : undefined;

  const onClick = (event) => {
    if (!href) {
      event.preventDefault();
      return;
    }

    trackAdPixel(clickPixel, additionalClickPixels);

    trackBrandingEvent(BRANDING_CLICK, { format: "frame" });

    createSponsorClick(token, clickId);
  };

  const linkProps = href
    ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
    : { component: "div" };

  // С заливкой: заливка на весь плеер, креатив целиком поверх, окном на видео
  const creative =
    loaded?.fill && size.width
      ? getFrameLayout({
          ...size,
          variant: loaded.variant,
        }).creative
      : null;

  const backgroundSrc = loaded ? loaded.fill || loaded.src : null;

  return (
    <>
      {/* Картинка рамки под видео (поджатие её открывает) */}
      <StyledButtonBase
        ref={bannerRef}
        component="div"
        tabIndex={-1}
        style={
          backgroundSrc
            ? {
                backgroundImage: `url('${backgroundSrc}')`,
                backgroundPosition: creative ? "center" : undefined,
              }
            : undefined
        }
        onClick={onClick}
      >
        {creative && (
          <CreativeLayer
            data-testid="branding-creative"
            style={{
              left: creative.x,
              top: creative.y,
              width: creative.width,
              height: creative.height,
              backgroundImage: `url('${loaded.src}')`,
            }}
          />
        )}
      </StyledButtonBase>
      {/* Кликабельная область рамки поверх шапки плеера, с вырезом под видео */}
      {active && (
        <FrameLink
          aria-label="advertising"
          style={size.width ? { clipPath: frameClipPath(size) } : undefined}
          onClick={onClick}
          {...linkProps}
        />
      )}
    </>
  );
}

SponsorCornerBanner.propTypes = {
  onStart: PropTypes.func,
  onFinish: PropTypes.func,
  cornerBanner: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ).isRequired,
    verticalThumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    fillThumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    link: PropTypes.string,
    token: PropTypes.string.isRequired,
    isClickable: PropTypes.bool,
    clickPixel: PropTypes.string,
    additionalClickPixels: PropTypes.arrayOf(PropTypes.string),
    impressionPixel: PropTypes.string,
    additionalImpressionPixels: PropTypes.arrayOf(PropTypes.string),
  }),
  setCornerBannerHeight: PropTypes.func,
};

SponsorCornerBanner.defaultProps = {
  onStart() {},
  onFinish() {},
  setCornerBannerHeight() {},
};
